import { useLayoutEffect, useRef } from "react";

type FitTextOptions = {
  maxFontPx?: number;
  minFontPx?: number;
  deps?: readonly unknown[];
};

export const useFitTextToContainerWidth = (
  text: string,
  options?: FitTextOptions,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const textEl = textRef.current;

    if (!containerEl || !textEl) return;

    const maxFontPx = options?.maxFontPx ?? 18;
    const minFontPx = options?.minFontPx ?? 12;
    const getRootFontPx = () => {
      const root = document.documentElement;
      const rootFontSize = window.getComputedStyle(root).fontSize;
      const parsed = Number.parseFloat(rootFontSize);

      return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
    };
    const toRem = (px: number) => {
      const rootPx = getRootFontPx();
      const rem = px / rootPx;
      // Keep output compact but stable (avoid long floats)
      const formatted = rem.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
      return `${formatted}rem`;
    };

    const fit = () => {
      const availableWidth = containerEl.clientWidth;

      if (!availableWidth) return;

      textEl.style.fontSize = toRem(maxFontPx);
      const contentWidth = textEl.scrollWidth;

      if (!contentWidth) return;

      const scale = Math.min(1, availableWidth / contentWidth);
      const nextFontPx = Math.max(minFontPx, Math.floor(maxFontPx * scale));
      textEl.style.fontSize = toRem(nextFontPx);
    };

    fit();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);

      return () => window.removeEventListener("resize", fit);
    }

    const observer = new ResizeObserver(() => fit());
    observer.observe(containerEl);

    return () => observer.disconnect();
  }, [text, options?.maxFontPx, options?.minFontPx, options?.deps]);

  return { containerRef, textRef };
};
